/**
 * ConversationItem Component
 * Displays a single conversation in the sidebar list with action menu
 */

import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { MessageSquare, MoreVertical, Share2, Pencil, Trash2 } from 'lucide-react'
import { Conversation } from '../../types'
import { isUserMessage, ChatHistoryItem } from '../../lib/actionCable'

interface ConversationItemProps {
  conversation: Conversation
  isActive?: boolean
  onShare?: (conversation: Conversation) => void
  onEdit?: (conversation: Conversation) => void
  onDelete?: (conversation: Conversation) => void
}

export function ConversationItem({ 
  conversation, 
  isActive = false,
  onShare,
  onEdit,
  onDelete
}: ConversationItemProps) {
  const navigate = useNavigate()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false)
      }
    }

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isMenuOpen])

  const handleClick = () => {
    if (!isMenuOpen) {
      navigate(`/c/${conversation.id}`)
    }
  }

  const handleMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsMenuOpen(!isMenuOpen)
  }

  const handleAction = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation()
    setIsMenuOpen(false)
    action()
  }

  /**
   * Get display name from conversation name, query, or first chat message
   */
  const getDisplayName = (): string => {
    // First priority: use conversation name if available
    if (conversation.name) {
      return conversation.name.length > 30
        ? conversation.name.substring(0, 30) + '...'
        : conversation.name
    }
    
    // Fallback to query
    if (conversation.query) {
      return conversation.query.length > 30
        ? conversation.query.substring(0, 30) + '...'
        : conversation.query
    }
    
    // Fallback to first user message from chat history
    if (conversation.chat_history && conversation.chat_history.length > 0) {
      const firstUserMessage = conversation.chat_history.find(msg => isUserMessage(msg as ChatHistoryItem))
      const messageText = firstUserMessage?.message || firstUserMessage?.content
      if (messageText) {
        return messageText.length > 30
          ? messageText.substring(0, 30) + '...'
          : messageText
      }
    }
    
    return 'New conversation'
  }

  /**
   * Format date to relative time string
   */
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return date.toLocaleDateString()
  }

  return (
    <div
      onClick={handleClick}
      className={`group relative px-3 py-2 rounded-lg transition-colors cursor-pointer ${
        isActive || isMenuOpen
          ? 'bg-gray-100 dark:bg-gray-800'
          : 'hover:bg-gray-100 dark:hover:bg-gray-800'
      }`}
    >
      <div className="flex items-start gap-2">
        <MessageSquare className="h-4 w-4 text-gray-400 dark:text-gray-500 mt-1.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1 mb-0.5">
            <p className="text-sm text-gray-700 dark:text-slate-200 truncate flex-1">
              {getDisplayName()}
            </p>
            {conversation.campaign_id && (
              <span 
                className="flex-shrink-0 w-2 h-2 bg-primary-500 rounded-full"
                title="Has campaign"
              />
            )}
            
            {/* 3-dot menu button */}
            <div ref={menuRef} className="relative flex-shrink-0">
              <button
                onClick={handleMenuClick}
                className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-all ${
                  isMenuOpen 
                    ? 'opacity-100 bg-gray-200 dark:bg-gray-700' 
                    : 'opacity-0 group-hover:opacity-100'
                }`}
                aria-label="Conversation actions"
              >
                <MoreVertical className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400" />
              </button>

              {/* Action Dropdown Menu */}
              {isMenuOpen && (
                <div className="absolute right-0 top-full mt-1 w-40 bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50">
                  <button
                    onClick={(e) => handleAction(e, () => onShare?.(conversation))}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    <Share2 className="h-4 w-4" />
                    Share
                  </button>
                  <button
                    onClick={(e) => handleAction(e, () => onEdit?.(conversation))}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    <Pencil className="h-4 w-4" />
                    Edit
                  </button>
                  <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                  <button
                    onClick={(e) => handleAction(e, () => onDelete?.(conversation))}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </button>
                </div>
              )}
            </div>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {formatDate(conversation.updated_at || conversation.created_at)}
          </p>
        </div>
      </div>
    </div>
  )
}

